import { LitElement, html, css, nothing } from 'lit';
import { router, navigator, outlet } from 'lit-element-router';
import { setPassiveTouchGestures } from '../utils/settings.js';
import '@trazit/tr-styling';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { updateMetadata } from 'pwa-helpers/metadata.js';

// Initialize lazy loading for heavy libraries (PDF.js, XLSX, Mammoth)
import './utils/lazy-libraries-init.js';
import { initCloudWatchRum } from './utils/cloudwatch-rum-init.js';
import {
  initMqttNotifications,
  subscribeUserChannel,
  subscribeToChannels,
  shutdownMqttNotifications,
} from './utils/mqtt-notifications-lazy.js';
import './utils/notification-service.js'; // Escucha platform-info / platform-error etc. y muestra toast
import { isSuccessOrErrorFromTrazitCoreHost } from './utils/trazit-notifications-harness.js';
import { resolveShellDisplayLang } from './utils/resolve-shell-display-lang.js';
import { DEFAULT_NOTIFICATION_BURST_MS } from './utils/notification-burst-collapse.js';
import { installMasterDataMergeFromActionResponses } from './utils/merge-action-master-data.js';
import { installIncidentApiRequestLog } from './utils/install-incident-api-request-log.js';
import { installIncidentContextGlobals } from './utils/incident-context-capture.js';
import { installIncidentContextS3UploadHook } from './utils/install-incident-context-s3-hook.js';

// This element is connected to the Redux store.
import { store } from '../redux/store';

// These are the actions needed by this element.
import { initMetadata, initConfig, setLang, setActivity } from '../redux/actions.js';
import { applyUrlParamsToSessionStorage, applyLocalModeFromUrl, getStoredUrlParamOverrides } from './utils/url-params-config.js';
import { loadAndLogPlatformVersionInfo } from './utils/platform-version-info.js';
import { validatePlatformConfigRuntime } from './utils/config-runtime-validation.js';
import { buildPlatformDocumentTitle } from './utils/platform-document-title.js';

//import '@material/mwc-snackbar';
//import '@material/mwc-circular-progress';
import '@material/web/progress/linear-progress.js';
//import '@material/web/snackbar/snackbar.js'; // 20240828 not implemented yet by material design v3


import './loading-logo';

//import { Client1Theme } from '@trazit/tr-styling/src/theme-cliente1.js';

// Saneamiento defensivo de localStorage del tema. Una sesion antigua pudo dejar
// `currentTheme` con valor corrupto (p.ej. "[object Object]" tras un setItem(obj) sin
// stringify) que tr-app.applyTheme y theme-notification-mixin propagaban sin validar al
// <tr-styling>, generando los warnings `El tema "[object Object]" no esta definido`.
// Limpiar al cargar el bundle, antes de cualquier consumidor: todos tienen fallback a
// 'trazit' cuando getItem devuelve null.
(function sanitizeThemeStorage() {
  try {
    const isCorruptTheme = (v) => typeof v !== 'string' || v === '[object Object]' || v === 'undefined' || v === 'null' || v === '';
    const rawTheme = localStorage.getItem('currentTheme');
    if (rawTheme !== null && isCorruptTheme(rawTheme)) {
      localStorage.removeItem('currentTheme');
    }
    const rawDark = localStorage.getItem('darkMode');
    if (rawDark !== null && rawDark !== 'true' && rawDark !== 'false') {
      localStorage.removeItem('darkMode');
    }
  } catch (_) { /* noop */ }
})();

installMasterDataMergeFromActionResponses();
installIncidentApiRequestLog();
installIncidentContextS3UploadHook();
installIncidentContextGlobals();

export class TrApp extends connect(store)(router(navigator(outlet(LitElement)))) {
  static get styles() {
    return [//Client1Theme,
      css`
                                    :host {
           display: block;
           position: relative;
           height: 100vh;
           width: 100%;
           background-color: var(--trazit-background-white, white);
           overflow: hidden;
           z-index: 1;
         }
      
      /* Dark mode background - use dark gray like #111827 */
      :host([dark-mode]) {
        background: var(--trazit-background-gradient, #111827) !important;
      }
      loading-logo {
        display: none;
      }
      loading-logo[active] {
        display: block;
      }        
      /* Hide default material spinner; branded loader handles visuals */
      md-circular-progress {
        display: none !important;
        position: fixed;
        top : 50%;
        left: calc(50% - 25px);
        color : #FFFFFF;
        color : rgba(36, 192, 235, 1);
        --mdc-theme-primary: rgba(36, 192, 235, 1);
      }
    `,];
  }

  render() {
    return html`
      <tr-home route='home' 
        @authorized=${()=>{
          const q = window.location.search;
          const keep = (q && (q.includes('mqtt=') || q.includes('voice=') || q.includes('disableScreenRecorder=') || q.includes('local='))) ? q : (()=>{ try { const s=sessionStorage.getItem('urlParamsDisable'); if(!s) return ''; const p=JSON.parse(s); const parts=[]; if(p.mqtt) parts.push('mqtt=false'); if(p.voice) parts.push('voice=false'); if(p.screenRecorder) parts.push('disableScreenRecorder=1'); if(p.local) parts.push('local=true'); return parts.length ? '?'+parts.join('&') : ''; } catch(_){ return ''; } })();
          this.navigate("/dashboard" + keep);
        }}
        @change-lang=${e=>store.dispatch(setLang(e.detail.lang))}
        @success=${this.setNotif} 
        @error=${this.setNotif}></tr-home>
      <tr-dashboard route='dashboard' .params=${this.params} .query=${this.query}
        @set-activity=${()=>store.dispatch(setActivity(true))}
        @change-lang=${e=>store.dispatch(setLang(e.detail.lang))}
        @completed=${this.completed}
        @success=${this.setNotif} 
        @error=${this.setNotif}></tr-dashboard>
      <tr-view404 route='view404'></tr-view404>
      <tr-resetpass route='resetpass'></tr-resetpass>
      <tr-wizards route='wizards' .params=${this.params} .query=${this.query}></tr-wizards>
      <tr-userrequest route='userRequest'></tr-userrequest>
      <tr-catalog-comparison route='catalog-comparison'></tr-catalog-comparison>
      <tr-workstation route='workstation' .params=${this.params}></tr-workstation>
      <mwc-snackbar></mwc-snackbar>
      <md-circular-progress indeterminate closed></md-circular-progress>
      <loading-logo id="loadingLogo"></loading-logo>
    `;
  }

  firstUpdated() {
    super.firstUpdated();

    // Persistir params de URL de inmediato (antes de cualquier async) por si hay auto-redirect
    applyUrlParamsToSessionStorage();
    
    this._manageServiceWorker();

    // Aplicar tema inicialmente (antes de cargar config para mostrar LCP image temprano)
    this.applyTheme();
    
    fetch("/config.json")
    .then(response => response.json())
    .then(config => {
      this.config = config;
      initCloudWatchRum(config);
      if (config.theme) {
        this.currentTheme = config.theme;
        this.applyTheme(); // Llama a applyTheme para asegurar que el tema se aplique
        this.requestUpdate();
      }
      
      // Cargar y mostrar información de versiones (incluye release date + backend)
      this.loadVersionInfo();
      
      // Parámetros URL para sesiones de testing/demo. Se persisten en sessionStorage para sobrevivir a la navegación (login → dashboard).
      const urlParams = new URLSearchParams(window.location.search);
      const disableMqtt = urlParams.get('disableMqtt') === '1' || urlParams.get('mqtt') === 'false';
      const disableVoice = urlParams.get('disableVoice') === '1' || urlParams.get('voice') === 'false';
      const disableScreenRecorder = urlParams.get('disableScreenRecorder') === '1';
      const enableDevMode = urlParams.get('devMode') === '1' || urlParams.get('supportMode') === '1';
      applyUrlParamsToSessionStorage();
      const stored = getStoredUrlParamOverrides();
      if (disableVoice || stored.voice) { config.voiceCommandNavigate = false; config.voiceCommandActions = false; }
      if (disableScreenRecorder || stored.screenRecorder) config.screenRecorder = false;
      // Modo dev: npm run dev (VITE_DEV_MODE) | import.meta.env.DEV | URL ?devMode=1 o ?supportMode=1 (para soporte en producción)
      config.devMode = config.devMode ?? (import.meta.env.VITE_DEV_MODE === 'true' || import.meta.env.DEV === true || enableDevMode || stored.devMode);
      validatePlatformConfigRuntime(config, {
        devMode: config.devMode,
        currentLang: localStorage.getItem('lang') || undefined,
      });
      applyLocalModeFromUrl(config);
      if (config.devMode) {
        console.warn('TRAZiT Dev Mode: herramientas de desarrollo activas');
        console.info('Debug AI: ejecuta debugAiConfig() en consola. Para logs detallados: sessionStorage.setItem("debugAi","1")');
      }
      // Broker/topics desde workstationMode (si la raíz no lo define); topics se unen.
      // MQTT solo en rutas /workstation/ si workstationMode.mqtt es true o { clientEnabled: true }; raíz puede tener mqtt: false.
      const onWorkstationPath =
        typeof window !== 'undefined' && /\/workstation\//.test(window.location.pathname);
      let workstationMqttDesired = false;
      const wm = config.workstationMode;
      if (wm && typeof wm === 'object' && !Array.isArray(wm)) {
        const rawMqtt = wm.mqtt;
        const mq =
          rawMqtt && typeof rawMqtt === 'object' && !Array.isArray(rawMqtt) ? rawMqtt : {};
        if (rawMqtt === false || mq.clientEnabled === false) {
          workstationMqttDesired = false;
        } else if (rawMqtt === true || mq.clientEnabled === true) {
          workstationMqttDesired = true;
        }
        if (!config.mqttBrokerUrl) {
          config.mqttBrokerUrl = wm.mqttBrokerUrl || mq.brokerUrl || mq.brokerWssUrl || mq.url;
        }
        if (!config.mqttUsername && (wm.mqttUsername || mq.username)) {
          config.mqttUsername = wm.mqttUsername || mq.username;
        }
        if (!config.mqttPassword && (wm.mqttPassword || mq.password)) {
          config.mqttPassword = wm.mqttPassword || mq.password;
        }
        const wmTopicList = wm.mqttNotificationsTopics || mq.notificationsTopics;
        const wmSingle = wm.mqttNotificationsTopic || mq.notificationsTopic;
        if (onWorkstationPath) {
          // En /workstation/ no mezclar con mqttNotificationsTopics de la raíz (plataforma); el listado definitivo lo arma tr-workstation (JSON + workstationMode).
          const onlyWm = Array.isArray(wmTopicList)
            ? wmTopicList.map((t) => String(t).trim()).filter(Boolean)
            : [];
          if (wmSingle != null && String(wmSingle).trim()) {
            onlyWm.push(String(wmSingle).trim());
          }
          config.mqttNotificationsTopics = [...new Set(onlyWm)];
          delete config.mqttNotificationsTopic;
        } else if (wmTopicList && Array.isArray(wmTopicList) && wmTopicList.length > 0) {
          const cur = Array.isArray(config.mqttNotificationsTopics) ? config.mqttNotificationsTopics : [];
          config.mqttNotificationsTopics = [
            ...new Set([...cur, ...wmTopicList].filter((t) => t != null && String(t).trim() !== ''))
          ];
        } else if (!config.mqttNotificationsTopics && (wm.mqttNotificationsTopics || mq.notificationsTopics)) {
          config.mqttNotificationsTopics = wm.mqttNotificationsTopics || mq.notificationsTopics;
        }
        if (!onWorkstationPath) {
          if (!config.mqttNotificationsTopic && (wm.mqttNotificationsTopic || mq.notificationsTopic)) {
            config.mqttNotificationsTopic = wm.mqttNotificationsTopic || mq.notificationsTopic;
          }
        }
      }
      if (disableMqtt || stored.mqtt) {
        config.mqttDisabled = true;
      } else if (workstationMqttDesired && onWorkstationPath && config.mqttBrokerUrl) {
        config.mqttDisabled = false;
      } else if (config.mqtt === false || config.mqttDisabled === true) {
        config.mqttDisabled = true;
      }
      store.dispatch(initConfig(config));
      this._applyPlatformDocumentTitle(config);
      const deferMqttToWorkstationView =
        onWorkstationPath && config.mqttBrokerUrl && !config.mqttDisabled;
      // MQTT de plataforma no se inicia en /workstation/ (topics solo desde JSON del puesto + workstationMode)
      if (config.mqttBrokerUrl && !config.mqttDisabled && !deferMqttToWorkstationView) {
        initMqttNotifications(config, (e) => this.setNotif(e)).then(({ connected, unsubscribe }) => {
          if (connected) this._mqttUnsubscribe = unsubscribe;
        });
      } else if (config.mqttDisabled) {
        if (this._mqttUnsubscribe) {
          this._mqttUnsubscribe();
          this._mqttUnsubscribe = null;
        }
        shutdownMqttNotifications();
      }
    })
    .catch(error => {
      console.error('Error al cargar config.json:', error);
    });     
    // Escuchar eventos personalizados para mostrar y ocultar el progreso circular
    this._handleShowProgressEvent = (ev) => {
      const src = ev?.composedPath?.()?.[0];
      const sourceTag = src?.tagName || src?.nodeName || 'unknown';
      
      this.showProgress(sourceTag);
    };
    this._handleHideProgressEvent = (ev) => {
      const src = ev?.composedPath?.()?.[0];
      const sourceTag = src?.tagName || src?.nodeName || 'unknown';
      
      this.hideProgress(sourceTag);
    };
    window.addEventListener('show-progress', this._handleShowProgressEvent);
    window.addEventListener('hide-progress', this._handleHideProgressEvent);
  
    this.waiting.closed = true;
    this.loadingLogo.removeAttribute('active');
    
    // ESCUCHAR EVENTOS DE ERROR DE PLATFORM-LOGIN
    this.initializePlatformLoginErrorListener();
    // Canales MQTT por sesión: userSession.mqttChannels + canal propio trazit/users/{userId}/messages
    window.addEventListener('subscribe-mqtt-user-channel', async (e) => {
      const { userId, mqttChannels } = e.detail || {};
      if (!this.config?.mqttBrokerUrl || this.config?.mqttDisabled) return;
      const cleanup = () => {
        if (this._mqttUserUnsubscribe) {
          this._mqttUserUnsubscribe();
          this._mqttUserUnsubscribe = null;
        }
        if (this._mqttChannelsUnsubscribe) {
          this._mqttChannelsUnsubscribe();
          this._mqttChannelsUnsubscribe = null;
        }
      };
      cleanup();
      const onNotif = (ev) => this.setNotif(ev);
      if (userId) {
        const r = await subscribeUserChannel(userId, onNotif, this.config);
        if (r?.subscribed) this._mqttUserUnsubscribe = r.unsubscribe;
      }
      if (Array.isArray(mqttChannels) && mqttChannels.length > 0) {
        const r = await subscribeToChannels(mqttChannels, onNotif);
        if (r?.subscribed) this._mqttChannelsUnsubscribe = r.unsubscribe;
      }
    });
    window.addEventListener('online', async () => {
      const toast = await this.ensureToastAvailable();
      if (toast && toast.shadowRoot) {
        toast.shadowRoot.querySelector(".mdc-snackbar__surface").style.backgroundColor = "rgb(51, 51, 51)";
        toast.labelText = "You are now online"
        toast.show()
      }
    })
    window.addEventListener('offline', async () => {
      const toast = await this.ensureToastAvailable();
      if (toast && toast.shadowRoot) {
        toast.shadowRoot.querySelector(".mdc-snackbar__surface").style.backgroundColor = "rgb(51, 51, 51)";
        toast.labelText = "You are now offline"
        toast.show()
      }
    })
  }

  _manageServiceWorker() {
    if (!('serviceWorker' in navigator)) return;

    const enableSw = typeof __TRAZIT_ENABLE_SW__ !== 'undefined' ? __TRAZIT_ENABLE_SW__ : true;

    if (!enableSw) {
      navigator.serviceWorker.getRegistrations()
        .then((regs) => Promise.all(regs.map((r) => r.unregister())))
        .catch(() => {});
      if ('caches' in window) {
        caches.keys()
          .then((names) => Promise.all(names.map((n) => caches.delete(n))))
          .catch(() => {});
      }
      return;
    }

    navigator.serviceWorker.register('/sw.js').then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
            window.location.reload();
          }
        };
      };
    }).catch((error) => {
      console.error('Error al registrar el Service Worker:', error);
    });
  }
  

  showProgress(source = 'unknown') {
  // // console.log("showProgress called 2");
    this._progressPending = (this._progressPending || 0) + 1;
    
    this.waiting.closed = false; // Mostrar el progreso
    this.loadingLogo.setAttribute('active', '');
  }
  
  hideProgress(source = 'unknown') {
    // // console.log("hideProgress called");
    this._progressPending = Math.max(0, (this._progressPending || 0) - 1);
    
    this.waiting.closed = true; // Ocultar el progreso
    this.loadingLogo.removeAttribute('active');
  }
  
  
  completed() {
    //console.log('completed'); // Asegúrate de que se está llamando
    this.hideProgress()
    // this.waiting.style.display = 'none'; // Alternativa 1
    // // o bien
    // // this.waiting.setAttribute('closed', ''); // Alternativa 2

    // this.loadingLogo.removeAttribute('active');
}


  async ensureToastAvailable() {
    if (this.toast === null) {
      this.toast = this.shadowRoot.querySelector('mwc-snackbar');
  
      if (!this.toast) {
        this.toast = document.createElement('mwc-snackbar');
        this.shadowRoot.appendChild(this.toast);
  
        // Esperar a que el shadowRoot esté disponible
        await new Promise((resolve) => requestAnimationFrame(resolve));
      }
    }
  
    // Esperar a que shadowRoot esté disponible
    while (!this.toast.shadowRoot) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }
  
    return this.toast;
  }
    

  async setNotif(e) {
    const src = e.detail || {};
    // Copia superficial: nunca reemplazar e.detail.message por string (rompe globalShowNotification).
    const d = { ...src };
    const lang = resolveShellDisplayLang(e.target);

    if (src.message && typeof src.message === 'object' && !Array.isArray(src.message)) {
      const m = src.message;
      for (const k of Object.keys(m)) {
        if (/^message_[a-z]{2,3}$/i.test(k) && d[k] === undefined) {
          d[k] = m[k];
        }
      }
      const fromLang = m['message_' + lang];
      const fromEn = m.message_en;
      const generic = m.message;
      d.message =
        (fromLang != null && String(fromLang).trim() !== '' ? String(fromLang) : '') ||
        (fromEn != null && String(fromEn).trim() !== '' ? String(fromEn) : '') ||
        (generic != null && String(generic).trim() !== '' ? String(generic) : '') ||
        '';
    }
    if (e.type === 'error' && d.is_error === undefined) {
      d.is_error = true;
    }

    if (d.waiting) {
      this.showProgress();
    }
    // Clases de vista extienden CommonCore como <elem-table>, <button-panel>, no como <common-core>.
    // Esos hosts ya mostraron custom-snackbar + notifs; no repetir mwc ni dashboard.
    if (isSuccessOrErrorFromTrazitCoreHost(e) && typeof window.globalShowNotification === 'function') {
      store.dispatch(setActivity(false));
      return;
    }
    const toast = await this.ensureToastAvailable();
    if (this.toast === null) {
      const sb = this.shadowRoot.querySelector('mwc-snackbar');
      if (!sb) {
        this.shadowRoot.appendChild(document.createElement('mwc-snackbar'));
      }
    }

    const messageForLang = d['message_' + lang] ?? d.message_en ?? d.message ?? '';
    if (d.log && this.dashboard?.style?.display !== 'none' && messageForLang) {
      this.dashboard.setNotif(e);
    }
    // Toast: string para mostrar (API success/error con detail.message y MQTT)
    const msgStr = typeof messageForLang === 'string' ? messageForLang : (d.message && typeof d.message === 'string' ? d.message : '');
    const msg = (msgStr && msgStr !== '.') ? msgStr : undefined;
    let toastColor = '';
    if (d.hasOwnProperty('is_error')) {
      if (d.is_error) {
        toastColor="var(--error-color)" 
        //this.toast.shadowRoot.querySelector(".mdc-snackbar__surface").style.backgroundColor = "#a33";
      } else {
        toastColor="var(--info-color)"
        //this.toast.shadowRoot.querySelector(".mdc-snackbar__surface").style.backgroundColor = "#0085ff";
      }
    } else {
              toastColor="var(--info-color)"
      //this.toast.shadowRoot.querySelector(".mdc-snackbar__surface").style.backgroundColor = "#0085ff";
    }

    
  if (msg) {
    const now = Date.now();
    const isDuplicateToast =
      msg === this._lastToastMsg && now - this._lastToastAt < DEFAULT_NOTIFICATION_BURST_MS;
    if (!isDuplicateToast) {
      const snackbarSurface = toast.shadowRoot.querySelector('.mdc-snackbar__surface');
      if (snackbarSurface) {
        snackbarSurface.style.backgroundColor = toastColor;
        toast.labelText = msg;
        toast.show();
        this._lastToastMsg = msg;
        this._lastToastAt = now;
      } else {
        console.error('No se encontró el elemento .mdc-snackbar__surface en el shadowRoot.');
      }
    }
  }
        
    store.dispatch(setActivity(false))
  }

  get dashboard() {
    return this.shadowRoot.querySelector("tr-dashboard")
  }
  get resetpass() {
    return this.shadowRoot.querySelector("tr-resetpass")
  }
  get userRequest() {
    return this.shadowRoot.querySelector("tr-userrequest")
  }

  get toast() {
    return this.shadowRoot.querySelector("mwc-snackbar")
  }

  get waiting() {
    return this.shadowRoot.querySelector("md-circular-progress");
  }

  get loadingLogo() {
    return this.shadowRoot.querySelector("#loadingLogo")
  }

  static get properties() {
    return {
      page: { type: String },
      params: { type: Object },
      query: { type: Object },
      data: { type: Object },
      title: { type: String },
      metadata: { type: Object },
      currentTheme: { type: String },
      lang: { type: String }
    };
  }

  static get routes() {
    return [{
      name: 'home',
      pattern: '',
      data: { 
        title: 'Home',
        description: "Trazit Platform",
        img: "./images/trazit-removebg.png"
      }
    }, {
      name: 'home',
      pattern: 'home',
      data: { 
        title: 'Home',
        description: "TRAZiT, Let's trace to the next level",
        img: "./images/logo.png"
      }
    }, {
      name: 'dashboard',
      pattern: 'dashboard'
    }, {
      name: 'dashboard',
      pattern: 'dashboard/:menu'
    }, {
      name: 'resetpass',
      pattern: 'resetpass'
    }, {
      name: 'wizards',
      pattern: 'wizards'
    }, {
      name: 'userrequest',
      pattern: 'userRequest'
    }, {
      name: 'catalog-comparison',
      pattern: 'catalog-comparison'
    }, {
      name: 'workstation',
      pattern: 'workstation/:workstationId'
    },{
      name: 'view404',
      pattern: '*'
    }];
  }

  // Helper function to normalize route names (case insensitive)
  normalizeRoute(route) {
    return route ? route.toLowerCase() : '';
  }

  router(route, params, query, data) {
    if (!route){return}
    this.activeRoute = this.normalizeRoute(route);
    this.params = params;
    this.query = query;
    this.data = data;
    
    // ✅ RUTAS PÚBLICAS: No requieren autenticación
    const PUBLIC_ROUTES = ['home', 'resetpass', 'wizards', 'userrequest', 'workstation', 'catalog-comparison'];
    
    let userSession = JSON.parse(sessionStorage.getItem("userSession"))
    if (userSession && userSession.finalToken) {
      // Usuario autenticado
      if (this.activeRoute == "home") {
        return history.forward()
      }
    } else {
      // Usuario NO autenticado
      if (!PUBLIC_ROUTES.includes(this.activeRoute)) {
        history.replaceState({}, '', '/');
        this.activeRoute = 'home';
        this._routeChanged();
        return
      }
    }
    this._routeChanged();
  }

  constructor() {
    super();
    // To force all event listeners for gestures to be passive.
    // See https://www.polymer-project.org/3.0/docs/devguide/settings#setting-passive-touch-gestures
    setPassiveTouchGestures(true);
    this.page = "";
    this.params = {};
    this.query = {};
    this.data = {};
    this.title = 'Trazit Platform';
    this.metadata = {};
    this.currentTheme = 'trazit';
    this._logoutSent = false;
    this._navType = this._getNavigationType();
    this._pageLoadTs = performance.now();
    this._isReloadInProgress = false;
    this._logoutBlocked = false;
    this._lastToastMsg = '';
    this._lastToastAt = 0;
    /** Workstation pide MQTT con `mqtt: false` en raíz: init diferido vía evento */
    this._handleRequestMqttInit = (ev) => {
      const cfg = ev.detail?.config;
      if (!cfg?.mqttBrokerUrl) {
        return;
      }
      if (cfg.mqttDisabled) {
        if (this._mqttUnsubscribe) {
          this._mqttUnsubscribe();
          this._mqttUnsubscribe = null;
        }
        shutdownMqttNotifications();
        return;
      }
      initMqttNotifications(cfg, (e) => this.setNotif(e)).then(({ connected, unsubscribe }) => {
        if (connected) this._mqttUnsubscribe = unsubscribe;
      });
    };
    // Event listeners para logout al cerrar pestaña
    // Nota: 'unload' está deprecado, usando 'beforeunload' y 'pagehide' (alternativa moderna)
    window.addEventListener('beforeunload', this.handleUnload.bind(this));
    window.addEventListener('pagehide', this.handleUnload.bind(this));
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('request-mqtt-init', this._handleRequestMqttInit);

    // Verifica si ya existe tr-styling en el documento
    if (!document.querySelector('tr-styling')) {
      const trStyling = document.createElement('tr-styling');
      trStyling.config = { theme: 'trazit' }; // O el tema por defecto
      document.body.appendChild(trStyling);
    }
    
    // Listen for theme changes from dashboard
    document.addEventListener('tr-app-theme-change', this._handleThemeChange.bind(this));
    
    // Listen for global theme changes
    document.addEventListener('theme-changed', this._onThemeChange.bind(this));
    
    // Apply initial theme
    this.applyTheme();
  }
  applyTheme() {
    // Check current theme and dark mode
    const currentTheme = localStorage.getItem('currentTheme') || 'trazit';
    const darkMode = localStorage.getItem('darkMode') === 'true';
    
    // Set theme attributes
    this.setAttribute('data-theme', currentTheme);
    if (darkMode) {
      this.setAttribute('dark-mode', '');
    } else {
      this.removeAttribute('dark-mode');
    }
    
    // Also handle tr-styling if available
    let trStyling = document.querySelector('tr-styling');
    if (!trStyling) {
      trStyling = document.createElement('tr-styling');
      document.body.appendChild(trStyling);
    }
    trStyling.config = { theme: currentTheme };
    
    // Removed LCP image visibility control - back to CSS background approach
    
    // También aplicar atributos al html/body para CSS
    if (darkMode) {
      document.documentElement.setAttribute('dark-mode', '');
      document.body.setAttribute('dark-mode', '');
    } else {
      document.documentElement.removeAttribute('dark-mode');
      document.body.removeAttribute('dark-mode');
    }
    
    // // console.log('🎨 TrApp - Theme applied:', { currentTheme, darkMode });
  }

  _handleThemeChange(event) {
    const { theme } = event.detail;
    // // console.log('🎨 TrApp - Received theme change:', theme);
    
    // Update current theme
    this.currentTheme = theme;
    
    // Apply theme to tr-styling
    this.applyTheme();
  }

  _onThemeChange() {
    this.applyTheme();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('request-mqtt-init', this._handleRequestMqttInit);
    // Remove event listeners
    document.removeEventListener('tr-app-theme-change', this._handleThemeChange.bind(this));
    document.removeEventListener('theme-changed', this._onThemeChange.bind(this));
  }

  handleUnload(event) {
    const isHidden = document.visibilityState === 'hidden' || document.hidden;
    const isReload = this._isPageReload();
    const elapsed = performance.now() - this._pageLoadTs;
    const suppressReload = isReload; // suprimir siempre en recarga
    this._isReloadInProgress = suppressReload;
    this._logoutBlocked = suppressReload; // no bloquear por isHidden en unload
    // No enviar logout aquí; se decide en visibilitychange cuando hidden
  }
  
  handleVisibilityChange(event) {
    const isHidden = document.visibilityState === 'hidden' || document.hidden;
    const isReload = this._isPageReload();
    const elapsed = performance.now() - this._pageLoadTs;
    const suppressReload = isReload; // suprimir en recarga
    const reloadFlag = this._isReloadInProgress || suppressReload;
    if (reloadFlag) {
      this._isReloadInProgress = false;
      this._logoutBlocked = true;
    } else {
      // Resetear bloqueo si no es recarga
      this._logoutBlocked = false;
    }
    if (reloadFlag) return;
    if (this._logoutBlocked) return;
    if (isHidden) {
      this.sendLogoutRequest('visibilitychange');
    }
  }
  
  async sendLogoutRequest(source) {
    // Prevenir múltiples ejecuciones
    const userSession = JSON.parse(sessionStorage.getItem("userSession"));
    const hasSession = !!(userSession && userSession.finalToken && userSession.backendUrl);
    if (this._logoutBlocked || this._logoutSent || !hasSession) return;

    this._logoutSent = true;

    const url = `${userSession.backendUrl}/app/AuthenticationAPIactions`;
    const params = new URLSearchParams();
    params.append('actionName', 'APP_SESSION_LOGOUT');
    params.append('finalToken', userSession.finalToken);
    const getUrl = `${url}?${params.toString()}`;

    // Prefer sendBeacon for unload/visibilitychange reliability
    try {
      const beaconSent = navigator.sendBeacon ? navigator.sendBeacon(getUrl) : false;
      if (!beaconSent) {
        await fetch(getUrl, { method: 'GET', keepalive: true });
      }
    } catch (error) {
      // swallow errors
    }
  }

  _isPageReload() {
    try {
      const navEntries = performance.getEntriesByType('navigation');
      if (navEntries && navEntries[0]?.type === 'reload') return true;
      if (performance.navigation && performance.navigation.type === performance.navigation.TYPE_RELOAD) return true;
    } catch (e) {
      // swallow
    }
    return false;
  }

  _getNavigationType() {
    try {
      const navEntries = performance.getEntriesByType('navigation');
      if (navEntries && navEntries[0]?.type) return navEntries[0].type;
      if (performance.navigation) {
        if (performance.navigation.type === performance.navigation.TYPE_RELOAD) return 'reload';
        if (performance.navigation.type === performance.navigation.TYPE_BACK_FORWARD) return 'back_forward';
      }
    } catch (e) {
      // swallow
    }
    return 'navigate';
  }

  _shouldSuppressLogoutForReload() {
    const isReloadNav = this._navType === 'reload' || this._isPageReload();
    const elapsed = performance.now() - this._pageLoadTs;
    // Sólo suprimir si es recarga y ocurre pronto tras el load (ej. refresh)
    return isReloadNav && elapsed < 8000;
  }

  updated(updates) {
    super.updated(updates);
    if (updates.has('page') || updates.has('params')) {
      this._pageChanged();
    }
  }

  async _routeChanged() {
    await this.updateComplete; // call it to wait the router complete updated
    // Show the corresponding page according to the route.
    //
    // If no page was found in the route data, page will be an empty string.
    // Show 'home' in that case. And if the page doesn't exist, show 'view404'.
    if (this.activeRoute == "") {
      this.page = "home";
    } else if (['home', 'dashboard', 'view404', 'resetpass', 'wizards', 'userrequest', 'catalog-comparison', 'workstation'].indexOf(this.activeRoute) !== -1) {
      this.page = this.activeRoute;
    } else {
      this.navigate("/view404");
    }
  }

  async _pageChanged() {
    await this.updateComplete; // call it to wait the router complete updated
    switch (this.page) {
      case 'home':
        import('./tr-home');
        break;
      case 'dashboard':
        import('./tr-dashboard-main.js');
        break;
      case 'view404':
        import('./tr-view404');
        break;
      case 'resetpass':
        import('./tr-resetpass');
        break;
      case 'wizards':
        import('./tr-wizards');
        break;
      case 'userrequest':
        import('./tr-userRequest');
        break;
      case 'catalog-comparison':
        import('./tr-catalog-comparison');
        break;
      case 'workstation':
        import('./tr-workstation');
        break;        
    }
    if (this.page == 'content') {
      store.dispatch(initMetadata({
        title: this.title +' - '+ this.params.menu
      }));
    } else {
      this._applyPlatformDocumentTitle();
    }
  }

  _applyPlatformDocumentTitle(config = this.config) {
    store.dispatch(initMetadata({
      title: buildPlatformDocumentTitle(config, this.title),
      image: this.data?.img || './images/trazit-removebg.png'
    }));
  }

  // updateMetadata(metadata) {
  //   if (metadata.title) {
  //     document.title = metadata.title;
  //   }
  //   if (metadata.description) {
  //     let metaDescription = document.querySelector('meta[name="description"]');
  //     if (!metaDescription) {
  //       metaDescription = document.createElement('meta');
  //       metaDescription.name = "description";
  //       document.head.appendChild(metaDescription);
  //     }
  //     metaDescription.content = metadata.description;
  //   }
  //   if (metadata.url) {
  //     let linkCanonical = document.querySelector('link[rel="canonical"]');
  //     if (!linkCanonical) {
  //       linkCanonical = document.createElement('link');
  //       linkCanonical.rel = "canonical";
  //       document.head.appendChild(linkCanonical);
  //     }
  //     linkCanonical.href = metadata.url;
  //   }
  // }
  

  stateChanged(state) {
    if (JSON.stringify(state.app.metadata) != JSON.stringify(this.metadata)) {
      this.metadata = state.app.metadata;
      updateMetadata({
        ...state.app.metadata,
        url: window.location.href
      })
    }
    if (this.lang != state.app.lang) {
      this.lang = state.app.lang;
    }
  }

  async loadVersionInfo() {
    await loadAndLogPlatformVersionInfo(this.config || {});
  }

  /**
   * Initialize event listener for platform-login error notifications
   */
  initializePlatformLoginErrorListener() {
    // Escuchar solo los eventos de error de platform-login
    document.addEventListener('login-error', this.handlePlatformLoginError.bind(this));
  }

  /**
   * Handle platform-login error events and convert to existing notification format
   */
  handlePlatformLoginError(event) {
    const { type, message, severity = 'error' } = event.detail;
    
    // Crear el evento de notificación compatible con tu sistema existente
    const notificationEvent = {
      detail: {
        message: message,
        message_en: message, // Para compatibilidad con tu sistema multiidioma
        is_error: true, // Siempre es error para platform-login
        severity: severity
      }
    };
    
    // Usar tu sistema de notificaciones existente
    this.setNotif(notificationEvent);
  }

}
customElements.define('tr-app', TrApp);
